import { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import SubjectSelect from "./SubjectSelect";
import PraticaSummaryChips from "./PraticaSummaryChips";
import NotePreview from "./NotePreview";
import { UserFormFields } from "../pages/studio/Operatori";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Loader2, Pencil } from "lucide-react";
import { toast } from "sonner";

const norm = (s) => (s || "").trim().toLowerCase().split(/\s+/).filter(Boolean).sort().join(" ");
const FIELDS = ["nome", "cognome", "data_nascita", "codice_fiscale", "indirizzo", "comune", "cap", "provincia", "cellulare", "telefono", "email", "otp_email", "pec", "note"];

function CollabUserDialog({ uid, onClose, onSaved }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/studio/utenti").then(({ data }) => {
      const u = (data || []).find((x) => x.id === uid);
      if (!u) { toast.error("Scheda utente non trovata"); onClose(); return; }
      setForm({ ...Object.fromEntries(FIELDS.map((f) => [f, u[f] || ""])), cellulari_extra: u.cellulari_extra || [], cellulari_etichette: u.cellulari_etichette || [], role: u.role, status: u.status || "active" });
    }).catch((e) => { toast.error(formatApiError(e.response?.data?.detail)); onClose(); });
  }, [uid, onClose]);
  const save = async () => {
    setSaving(true);
    try { await api.patch(`/studio/utenti/${uid}`, form); toast.success("Collaboratore aggiornato"); onSaved(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#121824] border-slate-700 text-slate-100 max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="collab-studio-edit-dialog">
        <DialogHeader><DialogTitle className="font-head">Modifica collaboratore</DialogTitle></DialogHeader>
        {form ? <UserFormFields form={form} onField={(k, v) => setForm((f) => ({ ...f, [k]: v }))} showPassword={false} prefix="collab-edit" />
          : <div className="py-8 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>}
        <DialogFooter className="mt-6 gap-2">
          <Button variant="outline" onClick={onClose} data-testid="collab-studio-edit-cancel" className="border-slate-700 text-slate-200 hover:bg-slate-800">Annulla</Button>
          <Button onClick={save} disabled={!form || saving} data-testid="collab-studio-edit-save" className="bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salva"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Campo "Collab. Studio" della pratica: selezione utente dello studio, matita per aprirne la scheda e recapiti.
export default function CollabStudioField({ value, onChange, testId }) {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = () => api.get("/studio/collaboratori").then(({ data }) => setList(data || [])).catch(() => setList([]));
  useEffect(() => { load(); }, []);
  const rec = value ? list.find((r) => norm(r.denominazione) === norm(value)) : null;
  const canEdit = !!rec && user?.role === "titolare";
  return (
    <div>
      <div className="flex items-center gap-1.5">
        <div className="flex-1"><SubjectSelect url="/studio/collaboratori" value={value} onChange={onChange} testId={testId} /></div>
        <button type="button" disabled={!canEdit} onClick={() => setEdit(rec.id)} data-testid={`${testId}-edit`}
          title={!rec ? "Nessun collaboratore selezionato" : (canEdit ? "Modifica scheda collaboratore" : "Solo il titolare può modificare la scheda")}
          className="mt-1.5 shrink-0 h-9 w-9 flex items-center justify-center rounded-md border border-teal-500/40 text-teal-600 hover:bg-teal-500/10 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"><Pencil className="h-4 w-4" /></button>
        <NotePreview note={rec?.note} testId={`${testId}-note-preview`} />
      </div>
      {rec && <PraticaSummaryChips rec={rec} testId={testId} waCategory="collaboratore_studio" />}
      {edit && <CollabUserDialog uid={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
    </div>
  );
}
